import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import Button from '../components/ui/Button'
import EmailLoginForm from '../components/auth/EmailLoginForm'

export default function LoginPage() {
  const { isAuthenticated, isLoading, signInWithGoogle, signInWithFacebook } = useAuth()
  const [signingInWith, setSigningInWith] = useState<'google' | 'facebook' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showOAuth, setShowOAuth] = useState(false)

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-text-secondary">Loading...</span>
        </div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const handleGoogleSignIn = async () => {
    setError(null)
    setSigningInWith('google')
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in')
      setSigningInWith(null)
    }
  }

  const handleFacebookSignIn = async () => {
    setError(null)
    setSigningInWith('facebook')
    try {
      await signInWithFacebook()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in')
      setSigningInWith(null)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="card p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold mb-2 text-center">DreamWright Console</h1>
        <p className="text-text-secondary mb-6 text-center">Sign in with your Meta email</p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <EmailLoginForm />

        <div className="flex items-center my-6">
          <div className="flex-1 h-px bg-surface-2" />
          <span className="px-3 text-xs text-text-muted">or</span>
          <div className="flex-1 h-px bg-surface-2" />
        </div>

        {showOAuth ? (
          <div className="space-y-3">
            <Button
              onClick={handleGoogleSignIn}
              loading={signingInWith === 'google'}
              disabled={signingInWith !== null}
              className="w-full flex items-center justify-center gap-2"
              variant="secondary"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </Button>

            <Button
              onClick={handleFacebookSignIn}
              loading={signingInWith === 'facebook'}
              disabled={signingInWith !== null}
              className="w-full flex items-center justify-center gap-2"
              variant="secondary"
              style={{ backgroundColor: '#1877F2' }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Sign in with Facebook
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setShowOAuth(true)}
            className="w-full text-sm text-text-muted hover:text-text-secondary transition-colors"
          >
            More sign-in options
          </button>
        )}
      </div>
    </div>
  )
}
